import React, { useState } from 'react';
import { formatValue } from '../utils/formatters';
import ContactDetailModal from './ContactDetailModal';

const ContactsModal = ({
    isOpen,
    onClose,
    contacts
}) => {
    const [selectedContact, setSelectedContact] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    if (!isOpen) {
        return null;
    }

    if (!contacts || typeof contacts !== 'object') {
        return null;
    }

    const contactsList = contacts.contacts || [];

    const handleContactClick = (contact) => {
        setSelectedContact(contact);
        setIsDetailModalOpen(true);
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedContact(null);
    };

    // Get contact display name
    const getContactName = (contact) => {
        if (contact.firstname || contact.lastname) {
            return `${contact.firstname || ''} ${contact.lastname || ''}`.trim() || 'Unnamed Contact';
        }
        if (contact.role) {
            return contact.role;
        }
        if (contact.email) {
            return contact.email;
        }
        if (contact.phone) {
            return contact.phone;
        }
        return 'Unnamed Contact';
    };

    // Get contact summary info
    const getContactSummary = (contact) => {
        const parts = [];
        if (contact.email) parts.push(`📧 ${contact.email}`);
        if (contact.phone) parts.push(`📞 ${contact.phone}`);
        if (contact.role) parts.push(`💼 ${contact.role}`);
        return parts.length > 0 ? parts.join(' • ') : 'No contact info';
    };

    // Get score color
    const getScoreColor = (score) => {
        if (score >= 7) return '#34d399'; // green
        if (score >= 5) return '#fbbf24'; // yellow
        if (score >= 3) return '#f97316'; // orange
        return '#f87171'; // red
    };

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(7, 7, 10, 0.92)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    padding: '24px'
                }}
                onClick={onClose}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '1400px',
                        maxHeight: '90vh',
                        backgroundColor: '#121212',
                        borderRadius: '10px',
                        boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
                        border: '1px solid rgba(100, 181, 246, 0.35)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '14px',
                            right: '14px',
                            backgroundColor: 'rgba(220, 53, 69, 0.92)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: '42px',
                            height: '42px',
                            fontSize: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            boxShadow: '0 8px 20px rgba(220, 53, 69, 0.45)',
                            transition: 'transform 0.2s ease, background-color 0.2s ease',
                            zIndex: 1
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(207, 46, 60, 0.95)';
                            e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.92)';
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        ×
                    </button>
                    <div
                        style={{
                            flex: 1,
                            overflow: 'auto',
                            backgroundColor: '#0c0c0c',
                            padding: '24px'
                        }}
                    >
                        <h3
                            style={{
                                margin: '0 0 24px 0',
                                color: '#e0e0e0',
                                fontWeight: '600'
                            }}
                        >
                            Contacts ({contactsList.length} {contactsList.length === 1 ? 'contact' : 'contacts'})
                        </h3>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            {contactsList.map((contact, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleContactClick(contact)}
                                    style={{
                                        backgroundColor: '#181818',
                                        border: '1px solid rgba(100, 181, 246, 0.12)',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#1f1f1f';
                                        e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#181818';
                                        e.currentTarget.style.borderColor = 'rgba(100, 181, 246, 0.12)';
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '16px',
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: '#e0e0e0',
                                                marginBottom: '8px'
                                            }}>
                                                {getContactName(contact)}
                                            </div>
                                            <div style={{
                                                fontSize: '13px',
                                                color: '#a0a0a0',
                                                marginBottom: '8px'
                                            }}>
                                                {getContactSummary(contact)}
                                            </div>
                                            {contact.source_url && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#909090',
                                                    marginTop: '8px'
                                                }}>
                                                    Source: <a 
                                                        href={contact.source_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ color: '#60a5fa', textDecoration: 'none' }}
                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        {contact.source_url}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'center',
                                            flexWrap: 'wrap'
                                        }}>
                                            {contact.overall_score !== undefined && contact.overall_score !== null && (
                                                <div style={{
                                                    backgroundColor: '#0c0c0c',
                                                    border: `1px solid ${getScoreColor(contact.overall_score)}`,
                                                    borderRadius: '6px',
                                                    padding: '8px 12px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    minWidth: '60px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '11px',
                                                        color: '#909090',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '4px',
                                                        fontWeight: '600',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Score
                                                    </div>
                                                    <div style={{
                                                        fontSize: '18px',
                                                        color: getScoreColor(contact.overall_score),
                                                        fontWeight: '700'
                                                    }}>
                                                        {formatValue(contact.overall_score)}
                                                    </div>
                                                </div>
                                            )}
                                            {contact.is_nonsensical && (
                                                <div style={{
                                                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                                    borderRadius: '6px',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    color: '#ef4444',
                                                    fontWeight: '600'
                                                }}>
                                                    Nonsensical
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {contactsList.length === 0 && (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#909090',
                                fontSize: '14px'
                            }}>
                                No contacts found
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Contact Detail Modal */}
            {selectedContact && (
                <ContactDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseDetailModal}
                    contact={selectedContact}
                />
            )}
        </>
    );
};

export default ContactsModal;

